import { useStoreState } from 'hooks';
import { Permission, PermissionsResponse } from 'store/permissions';
import { notification } from 'antd';
import axios from 'axios';
import { GALACTICA_BACKEND } from 'utils/config';
import { Role } from 'types/dt-graphql-apollo-v2';
import { useState } from 'react';

/** Checks whatever the current user has the privilege to do some specific actions based on its current Role and permissions.
 * @returns Returns a boolean with whatever an action or set of actions is available to the user.
 */
export const useAccessControl = () => {
  const permissions = useStoreState((state) => state.permissions.permissions);

  return (actions: Permission[]) =>
    actions?.every((p) => permissions?.includes(p));
};

/** Make an inquiry about the permissions available for the current user or for a specific Role.
 *
 * Returns a list of Permissions available for the current user in the currently logged-in tenant. The permissions will be based on the Role set for this user.
 *
 * If a Role is specified in the call, then a list with the Permissions available to that role will be returned.
 *  @returns Returns a list of Permissions avaialable to the user/to a specific Role.
 *  @returns The hook also returns a boolean to keep the loading state while fetching.
 *  Test line to check GCR to GAR check
 */
export const useGetPermissions = (): {
  getPermissions: (role?: Role) => Promise<Permission[] | undefined>;
  fetchingPermissions: boolean;
} => {
  const [fetchingPermissions, setFetchingPermissions] = useState(false);

  const token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);

  const getPermissions = async (role?: Role) => {
    setFetchingPermissions(true);

    const url = `${GALACTICA_BACKEND}/api/v1/permissions/${role || ''}`;

    try {
      const response = await axios.get<PermissionsResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          project: `${tenant}`,
          cluster: `${cluster}`,
        },
      });

      return response.data.permissions;
    } catch (err: any) {
      notification.error({
        message: 'Failed to fetch permissions.',
        description: err.toString(),
      });
    } finally {
      setFetchingPermissions(false);
    }
    return [];
  };
  return { getPermissions, fetchingPermissions };
};
